/**
 * Global select/dropdown — the one styled dropdown for the whole app.
 *
 * Custom-rendered (button + listbox popover) rather than a native <select>,
 * so the option menu carries KC3 styling too instead of the OS default:
 * 3px radius, elevated ring + shadow, indigo hover/selected states, check
 * on the active item.
 *
 *   <Select
 *     value={range}
 *     onChange={setRange}
 *     options={[
 *       { value: '7d', label: 'Last 7 days' },
 *       { value: '30d', label: 'Last 30 days' },
 *     ]}
 *     ariaLabel="Date range"
 *   />
 *
 * Or pass plain <option> children — their `value` prop and text content are
 * used, so existing call sites read exactly the same:
 *
 *   <Select …>
 *     <option value="">All agents</option>
 *     {agents.map((a) => <option value={a.id}>{a.name}</option>)}
 *   </Select>
 *
 * Keyboard: ↑↓ navigate, Enter/Space select, Esc close, opens on ↓.
 *
 * Internally renders a visually-hidden native <select> carrying the same
 * value/options — the semantic source of truth for assistive tech, form
 * semantics, and tests (combobox role / selectOptions / change events).
 * The styled button + listbox is the visual surface for mouse and touch.
 */
import { useEffect, useLayoutEffect, useRef, useState, type JSX, type ReactElement, type ReactNode } from 'react'
import { isValidElement } from 'react'
import { Check, ChevronDown, Search } from 'lucide-react'
import { CONTROL_SIZE, POPOVER_CLASSES, TRIGGER_CLASSES, popoverSide } from './control-styles.js'

export interface SelectOption {
  value: string
  label: string
  disabled?: boolean
}

interface SelectProps {
  value: string
  onChange: (value: string) => void
  /** Option list — alternative to passing <option> children. */
  options?: SelectOption[]
  /** Plain <option> children — wins over `options` when present. */
  children?: ReactNode
  /** Accessibility label (required — selects have no visible label in most uses). */
  ariaLabel: string
  disabled?: boolean
  /** 'md' (default) for standalone controls; 'sm' for compact filter bars. */
  size?: 'md' | 'sm'
  /** Shown in the trigger when `value` is '' and matches no option. */
  placeholder?: string
  /** Small caption rendered inside the trigger before the value ('Campaign'). */
  label?: string
  /** Adds a search box at the top of the menu (useful for long option lists). */
  filterable?: boolean
  /** Optional leading icon inside the trigger (e.g. a Download glyph). */
  leftIcon?: ReactNode
  /** Extra classes for the trigger wrapper — width overrides ('w-52'), margins. */
  className?: string
}

const SIZE = CONTROL_SIZE

/** Pull {value,label,disabled} pairs out of plain <option>/<optgroup> children. */
function optionsFromChildren(children: ReactNode): SelectOption[] {
  const out: SelectOption[] = []
  const walk = (node: ReactNode): void => {
    if (Array.isArray(node)) {
      node.forEach(walk)
      return
    }
    if (!isValidElement<{ value?: string; disabled?: boolean; children?: ReactNode }>(node)) return
    const el = node as ReactElement<{ value?: string; disabled?: boolean; children?: ReactNode }>
    // <optgroup> contributes no item itself — flatten its options.
    if (el.type === 'optgroup') {
      walk(el.props.children)
      return
    }
    const label = typeof el.props.children === 'string'
      ? el.props.children
      : Array.isArray(el.props.children)
        ? el.props.children.join('')
        : String(el.props.children ?? '')
    out.push({ value: el.props.value ?? label, label, ...(el.props.disabled ? { disabled: true } : {}) })
  }
  walk(children)
  return out
}

export default function Select({
  value,
  onChange,
  options,
  children,
  ariaLabel,
  disabled = false,
  size = 'md',
  placeholder,
  label,
  filterable = false,
  leftIcon,
  className = '',
}: SelectProps): JSX.Element {
  const s = SIZE[size]
  const items: SelectOption[] = children !== undefined ? optionsFromChildren(children) : (options ?? [])
  const selected = items.find((o) => o.value === value)

  const [open, setOpen] = useState(false)
  const [activeIdx, setActiveIdx] = useState(-1)
  const [alignRight, setAlignRight] = useState(false)
  const [query, setQuery] = useState('')
  const rootRef = useRef<HTMLDivElement>(null)
  const listRef = useRef<HTMLDivElement>(null)
  const filterRef = useRef<HTMLInputElement>(null)

  const q = query.trim().toLowerCase()
  const visibleItems = q === '' ? items : items.filter((o) => o.label.toLowerCase().includes(q))

  // Flip to right-anchored when the menu would overflow the viewport edge.
  useLayoutEffect(() => {
    if (!open) return
    const r = listRef.current?.getBoundingClientRect()
    if (r) setAlignRight(r.right > window.innerWidth - 8)
  }, [open])

  // Click-away + Escape close.
  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent): void => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [open])

  // Keep the active item scrolled into view.
  useEffect(() => {
    if (!open || activeIdx < 0) return
    listRef.current?.children[activeIdx]?.scrollIntoView({ block: 'nearest' })
  }, [open, activeIdx])

  // Focus the search box and reset it when the menu opens/closes.
  useEffect(() => {
    if (open && filterable) filterRef.current?.focus()
    if (!open) setQuery('')
  }, [open, filterable])

  const openMenu = (): void => {
    setActiveIdx(Math.max(0, visibleItems.findIndex((o) => o.value === value)))
    setOpen(true)
  }

  const choose = (v: string): void => {
    onChange(v)
    setOpen(false)
  }

  const onKeyDown = (e: React.KeyboardEvent): void => {
    if (disabled) return
    if (!open) {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === 'Enter' || e.key === ' ') {
        e.preventDefault()
        openMenu()
      }
      return
    }
    switch (e.key) {
      case 'Escape':
        e.preventDefault()
        setOpen(false)
        break
      case 'ArrowDown':
        e.preventDefault()
        setActiveIdx((i) => Math.min(visibleItems.length - 1, i + 1))
        break
      case 'ArrowUp':
        e.preventDefault()
        setActiveIdx((i) => Math.max(0, i - 1))
        break
      case 'Enter':
      case ' ':
        e.preventDefault()
        if (visibleItems[activeIdx]) choose(visibleItems[activeIdx].value)
        break
      case 'Tab':
        setOpen(false)
        break
    }
  }

  return (
    <div ref={rootRef} className={`group relative ${open ? 'z-30' : ''} ${className}`}>
      {/* sr-only, unfocusable by keyboard (the button handles keys) — AT and
          tests still reach it in the accessibility tree / DOM. */}
      <select
        aria-label={ariaLabel}
        className="sr-only"
        tabIndex={-1}
        disabled={disabled}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      >
        {children !== undefined
          ? children
          : items.map((o) => (
              <option key={o.value} value={o.value} disabled={o.disabled}>
                {o.label}
              </option>
            ))}
      </select>
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        disabled={disabled}
        onClick={() => (open ? setOpen(false) : openMenu())}
        onKeyDown={onKeyDown}
        className={`${TRIGGER_CLASSES} ${s.trigger} ${leftIcon ? s.padLeftIcon : s.padLeft}`}
      >
        {leftIcon && (
          <span className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 [&>svg]:h-3.5 [&>svg]:w-3.5">
            {leftIcon}
          </span>
        )}
        <span className="flex min-w-0 items-baseline gap-1.5">
          {label && (
            <span className="shrink-0 text-[10px] font-semibold uppercase tracking-wider text-slate-400">
              {label}
            </span>
          )}
          <span className={`truncate ${selected || value !== '' ? '' : 'text-slate-400'}`}>
            {selected?.label ?? (value !== '' ? value : placeholder ?? '')}
          </span>
        </span>
      </button>
      <ChevronDown
        className={`pointer-events-none absolute top-1/2 -translate-y-1/2 text-slate-400 transition-all duration-150 group-hover:text-slate-600 ${open ? 'rotate-180 text-indigo-500' : ''} ${s.icon}`}
        strokeWidth={2.25}
      />

      {open && (
        <div
          ref={listRef}
          role="listbox"
          className={`${POPOVER_CLASSES} ${popoverSide(alignRight)} max-h-60 min-w-full w-max overflow-auto py-1`}
        >
          {filterable && (
            <div className="sticky top-0 mb-1 border-b border-slate-100 bg-white px-2 pb-1.5 pt-1">
              <div className="relative">
                <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
                <input
                  ref={filterRef}
                  type="text"
                  value={query}
                  placeholder="Search…"
                  onChange={(e) => { setQuery(e.target.value); setActiveIdx(0) }}
                  onKeyDown={onKeyDown}
                  className={`w-full rounded-[3px] bg-slate-50 py-1.5 pl-7 pr-2 text-slate-700 ring-1 ring-slate-200 placeholder:text-slate-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500/50 ${s.popoverText}`}
                />
              </div>
            </div>
          )}
          {visibleItems.map((o, i) => {
            const isSelected = o.value === value
            const isActive = i === activeIdx
            return (
              <button
                key={`${o.value}-${i}`}
                type="button"
                role="option"
                aria-selected={isSelected}
                onMouseEnter={() => setActiveIdx(i)}
                onClick={() => !o.disabled && choose(o.value)}
                aria-disabled={o.disabled === true}
                className={`flex w-full items-center gap-2 text-left transition-colors ${s.item} ${
                  o.disabled
                    ? 'cursor-not-allowed text-slate-300'
                    : isActive
                      ? 'bg-indigo-50 text-indigo-700'
                      : 'text-slate-600'
                } ${isSelected ? 'font-semibold text-indigo-700' : ''}`}
              >
                <span className="min-w-0 flex-1 truncate">{o.label}</span>
                {isSelected && (
                  <Check className={`shrink-0 text-indigo-500 ${s.check}`} strokeWidth={2.5} />
                )}
              </button>
            )
          })}
          {visibleItems.length === 0 && (
            <div className={`px-3 py-2 text-slate-400 ${s.item}`}>
              {q !== '' ? 'No matches' : 'No options'}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
